import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/cn';
import { Button } from './Button';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
};

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const defaultFooter = (
    <div className="flex justify-end gap-2 border-t border-dash-outline-variant px-6 py-4">
      <Button variant="outline" onClick={onClose}>
        Cerrar
      </Button>
    </div>
  );

  return createPortal(
    <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4 sm:p-6">
      <button
        type="button"
        className="absolute inset-0 bg-dash-on-surface/50"
        aria-label="Cerrar modal"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
        className={cn(
          'relative z-10 w-full rounded-xl border border-dash-outline-variant bg-white shadow-lg',
          sizeClasses[size],
        )}
      >
        {(title || description) && (
          <div className="border-b border-dash-outline-variant px-6 py-4">
            {title && (
              <h2 id="modal-title" className="text-lg font-semibold text-dash-on-surface">
                {title}
              </h2>
            )}
            {description && (
              <p className="mt-1 text-sm text-dash-on-surface-variant">{description}</p>
            )}
          </div>
        )}
        {children && <div className="p-6">{children}</div>}
        {footer !== undefined ? footer : defaultFooter}
      </div>
    </div>,
    document.body,
  );
}
